import React from "react";
import PropTypes from "prop-types";
import tw from "twin.macro";
import styled from "styled-components";

const BtnGroup = styled.div`
  ${tw`relative inline-flex align-middle`}
`;

const DropdownToggle = styled.button`
  ${tw`whitespace-nowrap`}
  background: none;
  border: 1px solid ${({ isOpen }) => (isOpen ? "#33aa44" : "#eaedf3")};
  background: ${({ isOpen }) => (isOpen ? "white" : "none")};
  outline: none;
  box-shadow: none;
  padding: 10px 20px;
  margin-right: 10px;
  border-radius: 0.2rem;
  font-size: 0.875rem;
  line-height: 1.5;
  font-weight: 400;
  color: #212529;
  text-align: center;
  cursor: pointer;

  &::after {
    display: inline-block;
    margin-left: 0.255em;
    vertical-align: 0.125em;
    content: "";
    border-top: 0.3em solid;
    border-right: 0.3em solid transparent;
    border-bottom: 0;
    border-left: 0.3em solid transparent;
  }
`;

const DropdownMenu = styled.div`
  ${tw`absolute float-left text-left bg-white`}
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
  top: 100%;
  left: 0;
  z-index: 1000;
  min-width: 10rem;
  padding: 0.5rem 0;
  margin: 0.125rem 0 0;
  font-size: 1rem;
  color: #212529;
  list-style: none;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.25rem;
`;

const DropdownItem = styled.a`
  ${tw`block w-full whitespace-nowrap`}
  padding: 0.25rem 1.5rem;
  clear: both;
  font-weight: 400;
  color: ${({ isActive }) => (isActive ? "#fff" : "#212529")};
  background-color: ${({ isActive }) => (isActive ? "#007bff" : "transparent")};
  text-align: inherit;
  border: 0;
  text-decoration: none;

  &:hover,
  &:focus {
    color: ${({ isActive }) => (isActive ? "#fff" : "rgb(21.6, 24.25, 26.87)")};
    text-decoration: none;
    background-color: ${({ isActive }) => (isActive ? "#007bff" : "#e9ecef")};
  }
`;

const SearchWrap = styled.div``;

const handleFilter = (onClick, keyField) => (tag) => (event) => {
  event.preventDefault();
  return onClick(tag[keyField]);
};

export class Filters extends React.Component {
  state = {
    typedAhead: "",
    isOpen: false
  };

  containerRef = React.createRef();

  componentDidMount() {
    document.addEventListener("click", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    if (
      this.containerRef.current &&
      !this.containerRef.current.contains(event.target)
    ) {
      this.closeDropdown();
    }
  };

  toggleDropdown = (event) => {
    event.preventDefault();
    this.setState((prev) => ({ isOpen: !prev.isOpen }));
  };

  closeDropdown = () => {
    this.setState({ isOpen: false });
  };

  handleItemClick = (handler) => (event) => {
    handler(event);
    this.closeDropdown();
  };

  onTypeahead = (event) => {
    const typedAhead = this.state.typedAhead || "";

    const key = event.key;
    if (key == "Backspace" || key == "Delete") {
      this.setState({ typedAhead: typedAhead.slice(0, -1) });
    } else if (key.length == 1 && key.match(/[a-zA-Z0-9 ]/)) {
      this.setState({ typedAhead: typedAhead + key });
    } else if (key == "Escape") {
      this.setState({ typedAhead: "" });
    } else {
      // console.log("Doing nothing with", key)
    }
  };

  filterByTypeahead = (tags) =>
    this.state.typedAhead.length
      ? tags.filter((tag) =>
          tag[this.props.field || "name"].includes(this.state.typedAhead)
        )
      : tags;

  colorize = (name, matched) => {
    const pieces = name.split(matched);

    return pieces.map((piece, i) => (
      <React.Fragment key={i}>
        <span>{piece}</span>
        {i === pieces.length - 1 ? "" : <b>{matched}</b>}
      </React.Fragment>
    ));
  };

  colorizeWithTypeahead = (name) =>
    this.state.typedAhead.length
      ? this.colorize(name, this.state.typedAhead)
      : name;

  render() {
    const props = this.props;

    const tags = this.filterByTypeahead(props.tags);

    const keyField = props.keyField || "id";

    if (tags.length == 0) {
      tags.push({ [keyField]: "none", [props.field]: "No Results" });
    }

    const selectedTag =
      !props.tag || !props.tag.slug
        ? {}
        : tags.find((t) => t[keyField] === props.tag?.slug) || {} || {};

    return (
      <BtnGroup ref={this.containerRef} onKeyDown={this.onTypeahead}>
        <DropdownToggle
          type="button"
          isOpen={this.state.isOpen}
          onClick={this.toggleDropdown}
          aria-expanded={this.state.isOpen}
        >
          {props.label || "Type"}
          {selectedTag?.slug
            ? ": " + selectedTag[props.field || "name"] || ""
            : ""}
        </DropdownToggle>
        <DropdownMenu isOpen={this.state.isOpen}>
          {props.allowBlank ? (
            <DropdownItem
              href="/"
              key="blank"
              onClick={this.handleItemClick(
                handleFilter(props.onSelect, keyField)({})
              )}
            >
              &nbsp;
            </DropdownItem>
          ) : (
            ""
          )}
          {tags.map((tag, i) => (
            <DropdownItem
              key={tag[keyField] || i}
              href="/"
              isActive={
                props.tag && `${tag[keyField]}` === `${props.tag[keyField]}`
              }
              onClick={this.handleItemClick(
                handleFilter(props.onSelect, keyField)(tag)
              )}
            >
              {this.colorizeWithTypeahead(tag[props.field || "name"])}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </BtnGroup>
    );
  }
}

export class Search extends React.PureComponent {
  constructor(props) {
    super(props);
    this.searcherRef = React.createRef();
  }

  onChange = (event) => {
    event.preventDefault();
    this.props.onSearch(this.searcherRef.current.value);
  };

  render() {
    return (
      <SearchWrap>
        <input
          type="text"
          placeholder={this.props.placeholderText}
          ref={this.searcherRef}
          onChange={this.onChange}
        />
      </SearchWrap>
    );
  }
}

Filters.propTypes = {
  label: PropTypes.string,
  field: PropTypes.string,
  tags: PropTypes.array,
  tag: PropTypes.any,
  keyField: PropTypes.string,
  onSelect: PropTypes.func,
  allowBlank: PropTypes.bool
};

Search.propTypes = {
  placeholderText: PropTypes.string,
  onSearch: PropTypes.func
};

export default {
  Filters: Filters,
  Search: Search
};
